/* eslint-disable no-unused-vars */
import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { Column, useColumnOrder, useGlobalFilter, usePagination, useSortBy, useTable, useExpanded } from "react-table";

type TableRequestOptions = {
  hiddenColumns?: string[];
  columnOrder?: string[];
  disablePagination?: boolean;
  params?: Record<string, any> | Function;
  filters?: (globalFilter: any) => Record<string, any>;
  useDefaultSorting?: boolean;
};

function useTableQueryRequest(useQueryHook: any, columns: Column[], options: TableRequestOptions = {}) {
  const { hiddenColumns, columnOrder, params, filters, disablePagination = false, useDefaultSorting = true } = options;
  const [data, setData] = useState<any>([]);
  const [totalData, setTotalData] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryPage = +(searchParams.get("page") || 0);
  // Skip the initial mount so returning via back (?page=N) is not reset to page 1.
  const isFirstGlobalFilterEffect = useRef(true);

  const instance = useTable(
    {
      initialState: {
        globalFilter: {},
        ...(!disablePagination && {
          pageSize: +(Cookies.get("listing_sizeperpage") || 10),
          pageIndex: queryPage ? queryPage - 1 : 0,
        }),
        ...(hiddenColumns && { hiddenColumns }),
        ...(columnOrder && { columnOrder }),
        // Default sorting set to use descending by created date
        ...(useDefaultSorting && {
          sortBy: [
            {
              id: "created",
              desc: true,
            },
          ],
        }),
      },
      columns,
      data,
      manualSortBy: true,
      manualGlobalFilter: true,
      autoResetHiddenColumns: false,
      ...(!disablePagination && {
        manualPagination: true,
        pageCount: Math.ceil(totalData / +(Cookies.get("listing_sizeperpage") || 10)),
      }),
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useColumnOrder,
  );

  const {
    data: queryData,
    refetch,
    isLoading,
    isFetching,
    isError,
    dataUpdatedAt,
  } = useQueryHook({
    ...(!disablePagination && {
      page_size: instance.state.pageSize,
      page: instance.state.pageIndex + 1,
    }),
    ...(instance.state.sortBy[0] && {
      sort_by: `${instance.state.sortBy[0].id}:${instance.state.sortBy[0].desc ? "DESC" : "ASC"}`,
    }),
    ...(params && typeof params === "function" ? params(instance) : params),
    ...(filters && filters(instance.state.globalFilter)),
  });

  useEffect(() => {
    if (queryData) {
      setData(queryData?.results);
      setTotalData(queryData?.count);
    }
  }, [queryData]);

  useEffect(() => {
    if (!disablePagination) {
      instance.gotoPage(queryPage ? queryPage - 1 : 0);
    }
  }, [queryPage, disablePagination]);

  useEffect(() => {
    if (isFirstGlobalFilterEffect.current) {
      isFirstGlobalFilterEffect.current = false;
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", "1");
    setSearchParams(nextParams);
  }, [instance.state.globalFilter]);

  useEffect(() => {
    if (columnOrder && instance.state.columnOrder.join(",") !== columnOrder.join(",")) {
      instance.setColumnOrder(columnOrder);
    }
  }, [columnOrder]);

  const refetchRequest = (isDelete = false) => {
    if (isDelete && data.length === 1 && queryPage > 1) {
      searchParams.set("page", (queryPage - 1).toString());
      setSearchParams(searchParams);
    } else {
      refetch();
    }
  };

  const setCurrentPage = (page: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", page.toString());
    setSearchParams(nextParams);
  };

  const setItemsPerPage = (size: number) => {
    instance.setPageSize(size);
    setCurrentPage(1);
  };

  const { count, page } = queryData || {
    count: 0,
    page: 1,
    record_start: 0,
    record_end: 0,
  };

  return {
    instance,
    fetching: isFetching,
    loading: isLoading,
    isError,
    refetch: refetchRequest,
    refetchEndpoint: refetch,
    dataUpdatedAt,

    totalPages: Math.ceil(count / instance.state.pageSize),
    totalData,
    currentPage: page,
    itemsPerPage: instance.state.pageSize,
    setItemsPerPage,
    setCurrentPage,
  };
}

export default useTableQueryRequest;
